import {
  type CardRowInput,
  can,
  EXCEPTION_STATUSES,
  FINANCING_KINDS,
  type FinancingKind,
  type FinancingRowInput,
  financingCost,
  type ProjectCardView,
  type ProjectDetail,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { date, dateTime, percent, tenge, tengeShort } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';

export const projectCardQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['project-card', projectId],
    queryFn: () => api<ProjectCardView>(`/projects/${projectId}/card`),
  });

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));
const str = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

function Confirm({ label, by, at }: { label: string; by: string | null; at: string | null }) {
  return (
    <div
      className={clsx(
        'rounded-md border px-4 py-3',
        at ? 'border-success/40 bg-success-soft/40' : 'border-line',
      )}
    >
      <p className="text-[13px] font-medium">{label}</p>
      <p className="text-[12px] text-ink-2">{at ? `${by ?? '—'}, ${dateTime(at)}` : 'не подтверждено'}</p>
    </div>
  );
}

function Precontract({
  v,
  project,
  onSaved,
}: {
  v: ProjectCardView;
  project: ProjectDetail;
  onSaved: (v: ProjectCardView) => void;
}) {
  const me = useMe();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const canEdit = can(me.access, 'project.edit', target) || can(me.access, 'budget.edit', target);
  const canCalc = canEdit || can(me.access, 'estimate.edit', target);
  const canPto = can(me.access, 'psd.approve', target);
  const [f, setF] = useState({
    requestNote: v.requestNote ?? '',
    tenderNote: v.tenderNote ?? '',
    sourceNote: v.sourceNote ?? '',
    costCalcAmount: str(v.costCalcAmount),
    costCalcNote: v.costCalcNote ?? '',
    offerAmount: str(v.offerAmount),
    offerSentOn: v.offerSentOn ?? '',
  });
  useEffect(() => {
    setF({
      requestNote: v.requestNote ?? '',
      tenderNote: v.tenderNote ?? '',
      sourceNote: v.sourceNote ?? '',
      costCalcAmount: str(v.costCalcAmount),
      costCalcNote: v.costCalcNote ?? '',
      offerAmount: str(v.offerAmount),
      offerSentOn: v.offerSentOn ?? '',
    });
  }, [v]);
  const save = useMutation({
    mutationFn: () =>
      api<ProjectCardView>(`/projects/${project.id}/precontract`, {
        method: 'PATCH',
        body: {
          requestNote: f.requestNote.trim() || null,
          tenderNote: f.tenderNote.trim() || null,
          sourceNote: f.sourceNote.trim() || null,
          ...(v.moneyHidden
            ? {}
            : {
                costCalcAmount: f.costCalcAmount ? num(f.costCalcAmount) : null,
                offerAmount: f.offerAmount ? num(f.offerAmount) : null,
              }),
          costCalcNote: f.costCalcNote.trim() || null,
          offerSentOn: f.offerSentOn || null,
        },
      }),
    onSuccess: (r) => {
      onSaved(r);
      toast('Преддоговорная стадия сохранена');
    },
  });
  const act = useMutation({
    mutationFn: (action: 'confirm_calc' | 'confirm_volumes') =>
      api<ProjectCardView>(`/projects/${project.id}/precontract/action`, { body: { action } }),
    onSuccess: (r) => {
      onSaved(r);
      toast('Подтверждение сохранено');
    },
  });
  return (
    <Panel title="Преддоговорная стадия" meta="Регламент № 33П, пп. 2.1–2.5">
      <div className="grid gap-4 lg:grid-cols-3">
        <Field label="Запрос заказчика">
          <Textarea
            rows={3}
            disabled={!canEdit}
            value={f.requestNote}
            onChange={(e) => setF({ ...f, requestNote: e.target.value })}
          />
        </Field>
        <Field label="ТЗ или тендерная документация">
          <Textarea
            rows={3}
            disabled={!canEdit}
            value={f.tenderNote}
            onChange={(e) => setF({ ...f, tenderNote: e.target.value })}
          />
        </Field>
        <Field label="Исходные данные">
          <Textarea
            rows={3}
            disabled={!canEdit}
            value={f.sourceNote}
            onChange={(e) => setF({ ...f, sourceNote: e.target.value })}
          />
        </Field>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_2fr_1fr_1fr]">
        <Field label="Расчёт себестоимости, ₸ с НДС">
          <Input
            inputMode="decimal"
            disabled={!canEdit || v.moneyHidden}
            value={v.moneyHidden ? 'скрыто' : f.costCalcAmount}
            onChange={(e) => setF({ ...f, costCalcAmount: e.target.value })}
          />
        </Field>
        <Field label="Основание расчёта" hint="Изменение расчёта снимает подтверждения">
          <Input
            disabled={!canEdit}
            value={f.costCalcNote}
            onChange={(e) => setF({ ...f, costCalcNote: e.target.value })}
          />
        </Field>
        <Field label="КП заказчику, ₸">
          <Input
            inputMode="decimal"
            disabled={!canEdit || v.moneyHidden}
            value={v.moneyHidden ? 'скрыто' : f.offerAmount}
            onChange={(e) => setF({ ...f, offerAmount: e.target.value })}
          />
        </Field>
        <Field label="КП отправлено">
          <Input
            type="date"
            disabled={!canEdit}
            value={f.offerSentOn}
            onChange={(e) => setF({ ...f, offerSentOn: e.target.value })}
          />
        </Field>
      </div>
      {canEdit ? (
        <div className="mt-4">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
        </div>
      ) : null}
      <ErrorBox error={save.error} className="mt-3" />
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Confirm label="Расчёт подтвердил исполнитель" by={v.costCalcByName} at={v.costCalcAt} />
        <Confirm
          label="Объёмы, люди, техника, материалы и субподряд — ПТО"
          by={v.volumesByName}
          at={v.volumesAt}
        />
        <div
          className={clsx(
            'rounded-md px-4 py-3 text-[13px]',
            v.calcUsable ? 'bg-success-soft' : 'bg-warning-soft',
          )}
        >
          {v.calcUsable
            ? 'Расчёт подтверждён и используется: в карточке проекта и финансовом плане.'
            : 'Без двух подтверждений расчёт не используется (ПФ-21).'}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {canCalc && !v.costCalcAt ? (
          <Button
            loading={act.isPending && act.variables === 'confirm_calc'}
            onClick={() => act.mutate('confirm_calc')}
          >
            Подтвердить расчёт (исполнитель)
          </Button>
        ) : null}
        {canPto && !v.volumesAt ? (
          <Button
            loading={act.isPending && act.variables === 'confirm_volumes'}
            onClick={() => act.mutate('confirm_volumes')}
          >
            Подтвердить объёмы (ПТО)
          </Button>
        ) : null}
      </div>
      <ErrorBox error={act.error} className="mt-3" />
    </Panel>
  );
}

function Exception({
  v,
  project,
  onSaved,
}: {
  v: ProjectCardView;
  project: ProjectDetail;
  onSaved: (v: ProjectCardView) => void;
}) {
  const me = useMe();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const canAsk =
    can(me.access, 'project.edit', target) || can(me.access, 'procurement.request.create', target);
  const e = v.exception;
  // Решает Директор (право утверждать бюджет на уровне юрлица или компании), но не автор записки
  const canDecide =
    can(me.access, 'budget.approve', { legalEntityId: project.legalEntityId }) &&
    (e.byId !== me.user.id || Boolean(me.tenant?.isOwner));
  const [f, setF] = useState({ reason: '', scope: '', cost: '', terms: '', risks: '' });
  const [comment, setComment] = useState('');
  const m = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<ProjectCardView>(`/projects/${project.id}/precontract/action`, { body }),
    onSuccess: (r) => {
      onSaved(r);
      toast(`Служебная записка: ${EXCEPTION_STATUSES[r.exception.status].toLowerCase()}`);
    },
  });
  if (!v.preliminary && e.status === 'none') return null;
  return (
    <Panel
      title="Обязательства до подписания договора"
      meta="служебная записка и одобрение Директора, пп. 2.8–2.9"
    >
      {e.status !== 'none' ? (
        <div className="mb-4 flex flex-col gap-2 text-[14px]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              tone={e.status === 'approved' ? 'success' : e.status === 'rejected' ? 'danger' : 'warning'}
            >
              {EXCEPTION_STATUSES[e.status]}
            </Badge>
            <span className="text-ink-2">
              {e.byName ?? '—'}, {dateTime(e.at)}
              {e.decisionAt ? ` · решение: ${e.decisionByName ?? '—'}, ${dateTime(e.decisionAt)}` : ''}
            </span>
          </div>
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[180px_1fr]">
            <dt className="text-ink-2">Обоснование</dt>
            <dd>{e.reason}</dd>
            <dt className="text-ink-2">Объём обязательств</dt>
            <dd>{e.scope}</dd>
            <dt className="text-ink-2">Стоимость</dt>
            <dd className="num">{tenge(e.cost)}</dd>
            <dt className="text-ink-2">Сроки</dt>
            <dd>{e.terms}</dd>
            <dt className="text-ink-2">Риски</dt>
            <dd>{e.risks}</dd>
            {e.comment ? (
              <>
                <dt className="text-ink-2">Комментарий Директора</dt>
                <dd>{e.comment}</dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}
      {e.status === 'pending' && canDecide ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Комментарий" className="min-w-[280px] flex-1">
            <Input value={comment} onChange={(x) => setComment(x.target.value)} />
          </Field>
          <Button
            variant="primary"
            loading={m.isPending}
            onClick={() => m.mutate({ action: 'approve_exception', comment: comment || undefined })}
          >
            Одобрить
          </Button>
          <Button
            variant="danger"
            disabled={!comment.trim()}
            loading={m.isPending}
            onClick={() => m.mutate({ action: 'reject_exception', comment })}
          >
            Отклонить
          </Button>
        </div>
      ) : null}
      {v.preliminary && canAsk && e.status !== 'pending' && e.status !== 'approved' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Обоснование" className="sm:col-span-2">
            <Textarea rows={2} value={f.reason} onChange={(x) => setF({ ...f, reason: x.target.value })} />
          </Field>
          <Field label="Объём обязательств">
            <Textarea rows={2} value={f.scope} onChange={(x) => setF({ ...f, scope: x.target.value })} />
          </Field>
          <Field label="Риски">
            <Textarea rows={2} value={f.risks} onChange={(x) => setF({ ...f, risks: x.target.value })} />
          </Field>
          <Field label="Стоимость обязательств, ₸">
            <Input
              inputMode="decimal"
              value={f.cost}
              onChange={(x) => setF({ ...f, cost: x.target.value })}
            />
          </Field>
          <Field label="Сроки">
            <Input value={f.terms} onChange={(x) => setF({ ...f, terms: x.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <Button
              variant="primary"
              loading={m.isPending}
              disabled={!f.reason.trim() || !f.scope.trim() || !f.cost || !f.terms.trim() || !f.risks.trim()}
              onClick={() => m.mutate({ action: 'request_exception', ...f, cost: num(f.cost) })}
            >
              Отправить Директору
            </Button>
          </div>
        </div>
      ) : null}
      <ErrorBox error={m.error} className="mt-3" />
    </Panel>
  );
}

interface FinRow {
  key: string;
  kind: FinancingKind;
  source: string;
  amount: string;
  ratePct: string;
  dateFrom: string;
  dateTo: string;
  note: string;
}

function Financing({
  v,
  project,
  onSaved,
}: {
  v: ProjectCardView;
  project: ProjectDetail;
  onSaved: (v: ProjectCardView) => void;
}) {
  const me = useMe();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const canEdit = can(me.access, 'finance.edit', target) && !v.moneyHidden;
  const toRows = (): FinRow[] =>
    v.financing.map((f) => ({
      key: f.id,
      kind: f.kind,
      source: f.source ?? '',
      amount: str(f.amount),
      ratePct: str(f.ratePct),
      dateFrom: f.dateFrom ?? '',
      dateTo: f.dateTo ?? '',
      note: f.note ?? '',
    }));
  const [rows, setRows] = useState<FinRow[]>(toRows);
  // biome-ignore lint/correctness/useExhaustiveDependencies: пересобираем строки при новом ответе сервера
  useEffect(() => setRows(toRows()), [v]);
  const save = useMutation({
    mutationFn: () =>
      api<ProjectCardView>(`/projects/${project.id}/financing`, {
        method: 'PUT',
        body: {
          rows: rows.map(
            (r): FinancingRowInput => ({
              kind: r.kind,
              source: r.source.trim() || null,
              amount: num(r.amount),
              ratePct: r.ratePct ? num(r.ratePct) : null,
              dateFrom: r.dateFrom || null,
              dateTo: r.dateTo || null,
              note: r.note.trim() || null,
            }),
          ),
        },
      }),
    onSuccess: (r) => {
      onSaved(r);
      toast('Условия финансирования сохранены');
    },
  });
  const set = (k: string, patch: Partial<FinRow>) =>
    setRows((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const total = rows.reduce(
    (s, r) =>
      s +
      (financingCost(
        num(r.amount) || 0,
        r.ratePct ? num(r.ratePct) : null,
        r.dateFrom || null,
        r.dateTo || null,
      ).cost ?? 0),
    0,
  );
  return (
    <Panel
      title="Условия финансирования"
      meta="вносит финансовый менеджер, согласование не нужно (п. 2.4)"
      bodyClassName="p-0"
      actions={
        canEdit ? (
          <>
            <Button
              variant="ghost"
              icon={<Plus className="size-4" />}
              onClick={() =>
                setRows([
                  ...rows,
                  {
                    key: `n${Date.now()}`,
                    kind: 'bg',
                    source: '',
                    amount: '',
                    ratePct: '',
                    dateFrom: '',
                    dateTo: '',
                    note: '',
                  },
                ])
              }
            >
              Строка
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Сохранить
            </Button>
          </>
        ) : null
      }
    >
      <ErrorBox error={save.error} className="m-4" />
      {rows.length ? (
        <ScrollX>
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-2 pl-5 font-medium">Вид</th>
                <th className="py-2.5 pr-2 font-medium">Банк</th>
                <th className="py-2.5 pr-2 text-right font-medium">Сумма, ₸</th>
                <th className="py-2.5 pr-2 text-right font-medium">Ставка, %</th>
                <th className="py-2.5 pr-2 font-medium">С</th>
                <th className="py-2.5 pr-2 font-medium">По</th>
                <th className="py-2.5 pr-2 text-right font-medium">Дней</th>
                <th className="py-2.5 pr-2 text-right font-medium">Стоимость</th>
                <th className="py-2.5 pr-5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = financingCost(
                  num(r.amount) || 0,
                  r.ratePct ? num(r.ratePct) : null,
                  r.dateFrom || null,
                  r.dateTo || null,
                );
                return (
                  <tr key={r.key} className="border-t border-line">
                    <td className="py-1.5 pr-2 pl-5">
                      <Select
                        className="min-w-[200px]"
                        disabled={!canEdit}
                        value={r.kind}
                        onChange={(e) => set(r.key, { kind: e.target.value as FinancingKind })}
                      >
                        {Object.entries(FINANCING_KINDS).map(([k, t]) => (
                          <option key={k} value={k}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        value={r.source}
                        onChange={(e) => set(r.key, { source: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        inputMode="decimal"
                        className="text-right"
                        value={v.moneyHidden ? 'скрыто' : r.amount}
                        onChange={(e) => set(r.key, { amount: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        inputMode="decimal"
                        className="w-20 text-right"
                        value={r.ratePct}
                        onChange={(e) => set(r.key, { ratePct: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        type="date"
                        value={r.dateFrom}
                        onChange={(e) => set(r.key, { dateFrom: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        type="date"
                        value={r.dateTo}
                        onChange={(e) => set(r.key, { dateTo: e.target.value })}
                      />
                    </td>
                    <td className="num py-1.5 pr-2 text-right">{c.days ?? '—'}</td>
                    <td className="num py-1.5 pr-2 text-right">{v.moneyHidden ? '—' : tengeShort(c.cost)}</td>
                    <td className="py-1.5 pr-5 text-right">
                      {canEdit ? (
                        <IconButton
                          label="Удалить строку"
                          onClick={() => setRows(rows.filter((x) => x.key !== r.key))}
                        >
                          <Trash2 className="size-4" />
                        </IconButton>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t border-line font-medium">
                <td colSpan={7} className="py-2 pr-2 pl-5">
                  Стоимость финансирования: сумма × ставка / 365 × дни
                </td>
                <td className="num py-2 pr-2 text-right">{v.moneyHidden ? '—' : tengeShort(total)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </ScrollX>
      ) : (
        <p className="p-5 text-[14px] text-ink-2">Кредитов и банковских гарантий по проекту нет.</p>
      )}
    </Panel>
  );
}

interface CardRow {
  key: string;
  article: string;
  counterpartyBin: string;
  counterpartyName: string;
  name: string;
  unit: string;
  quantity: string;
  purchasePrice: string;
  salePrice: string;
}

function Card({
  v,
  project,
  onSaved,
}: {
  v: ProjectCardView;
  project: ProjectDetail;
  onSaved: (v: ProjectCardView) => void;
}) {
  const costArticles = useCostArticles();
  const me = useMe();
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  const canEdit =
    (can(me.access, 'project.edit', target) || can(me.access, 'budget.edit', target)) && !v.moneyHidden;
  const toRows = (): CardRow[] =>
    v.card.map((r) => ({
      key: r.id,
      article: r.article as CardRow['article'],
      counterpartyBin: r.counterpartyBin ?? '',
      counterpartyName: r.counterpartyName ?? '',
      name: r.name,
      unit: r.unit,
      quantity: str(r.quantity),
      purchasePrice: str(r.purchasePrice),
      salePrice: str(r.salePrice),
    }));
  const [rows, setRows] = useState<CardRow[]>(toRows);
  // biome-ignore lint/correctness/useExhaustiveDependencies: пересобираем строки при новом ответе сервера
  useEffect(() => setRows(toRows()), [v]);
  const save = useMutation({
    mutationFn: () =>
      api<ProjectCardView>(`/projects/${project.id}/card`, {
        method: 'PUT',
        body: {
          rows: rows.map(
            (r): CardRowInput => ({
              article: r.article,
              counterpartyBin: r.counterpartyBin.trim() || null,
              counterpartyName: r.counterpartyName.trim() || null,
              name: r.name.trim(),
              unit: r.unit.trim(),
              quantity: num(r.quantity) || 0,
              purchasePrice: r.purchasePrice ? num(r.purchasePrice) : null,
              salePrice: r.salePrice ? num(r.salePrice) : null,
            }),
          ),
        },
      }),
    onSuccess: (r) => {
      onSaved(r);
      toast('Карточка проекта сохранена');
    },
  });
  const set = (k: string, patch: Partial<CardRow>) =>
    setRows((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const sum = (r: CardRow, f: 'purchasePrice' | 'salePrice') =>
    r[f] ? num(r[f]) * (num(r.quantity) || 0) : 0;
  const purchase = rows.reduce((s, r) => s + sum(r, 'purchasePrice'), 0);
  const sale = rows.reduce((s, r) => s + sum(r, 'salePrice'), 0);
  return (
    <Panel
      title="Карточка проекта"
      meta="форма приложения 1 к приказу № 29П"
      bodyClassName="p-0"
      actions={
        <>
          <a
            href={`/api/v1/projects/${project.id}/card/export`}
            className="inline-flex h-10 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] text-ink hover:underline"
          >
            <Download className="size-4" /> Excel
          </a>
          {canEdit ? (
            <>
              <Button
                variant="ghost"
                icon={<Plus className="size-4" />}
                onClick={() =>
                  setRows([
                    ...rows,
                    {
                      key: `n${Date.now()}`,
                      article: 'Материалы',
                      counterpartyBin: '',
                      counterpartyName: '',
                      name: '',
                      unit: '',
                      quantity: '',
                      purchasePrice: '',
                      salePrice: '',
                    },
                  ])
                }
              >
                Строка
              </Button>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
                Сохранить
              </Button>
            </>
          ) : null}
        </>
      }
    >
      <ErrorBox error={save.error} className="m-4" />
      {rows.length ? (
        <ScrollX>
          <table className="w-full min-w-[1200px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-2 pl-5 font-medium">Статья расходов</th>
                <th className="py-2.5 pr-2 font-medium">БИН</th>
                <th className="py-2.5 pr-2 font-medium">Контрагент</th>
                <th className="py-2.5 pr-2 font-medium">Плановая номенклатура</th>
                <th className="py-2.5 pr-2 font-medium">Ед.</th>
                <th className="py-2.5 pr-2 text-right font-medium">Кол-во</th>
                <th className="py-2.5 pr-2 text-right font-medium">Цена закупа</th>
                <th className="py-2.5 pr-2 text-right font-medium">Цена продажи</th>
                <th className="py-2.5 pr-2 text-right font-medium">Сумма закупа</th>
                <th className="py-2.5 pr-2 text-right font-medium">Сумма продажи</th>
                <th className="py-2.5 pr-5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-line align-top">
                  <td className="py-1.5 pr-2 pl-5">
                    <Select
                      disabled={!canEdit}
                      value={r.article}
                      onChange={(e) => set(r.key, { article: e.target.value as CardRow['article'] })}
                    >
                      {costArticles.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="w-32"
                      inputMode="numeric"
                      value={r.counterpartyBin}
                      onChange={(e) => set(r.key, { counterpartyBin: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      value={r.counterpartyName}
                      onChange={(e) => set(r.key, { counterpartyName: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="min-w-[220px]"
                      value={r.name}
                      onChange={(e) => set(r.key, { name: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="w-16"
                      value={r.unit}
                      onChange={(e) => set(r.key, { unit: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="w-24 text-right"
                      inputMode="decimal"
                      value={r.quantity}
                      onChange={(e) => set(r.key, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="w-32 text-right"
                      inputMode="decimal"
                      value={r.purchasePrice}
                      onChange={(e) => set(r.key, { purchasePrice: e.target.value })}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      disabled={!canEdit}
                      className="w-32 text-right"
                      inputMode="decimal"
                      value={r.salePrice}
                      onChange={(e) => set(r.key, { salePrice: e.target.value })}
                    />
                  </td>
                  <td className="num py-3 pr-2 text-right whitespace-nowrap">
                    {tenge(sum(r, 'purchasePrice'))}
                  </td>
                  <td className="num py-3 pr-2 text-right whitespace-nowrap">{tenge(sum(r, 'salePrice'))}</td>
                  <td className="py-1.5 pr-5 text-right">
                    {canEdit ? (
                      <IconButton
                        label="Удалить строку"
                        onClick={() => setRows(rows.filter((x) => x.key !== r.key))}
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    ) : null}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-ink/30 font-semibold">
                <td colSpan={8} className="py-2.5 pr-2 pl-5">
                  Итого{sale > 0 ? `, маржа ${percent(((sale - purchase) / sale) * 100)}` : ''}
                </td>
                <td className="num py-2.5 pr-2 text-right whitespace-nowrap">{tenge(purchase)}</td>
                <td className="num py-2.5 pr-2 text-right whitespace-nowrap">{tenge(sale)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </ScrollX>
      ) : (
        <p className="p-5 text-[14px] text-ink-2">
          Строк нет. Карточка собирается из сметы и подтверждённого расчёта себестоимости: статья расходов,
          БИН контрагента, номенклатура, количество, плановая цена закупа и продажная цена.
        </p>
      )}
    </Panel>
  );
}

export function ProjectCardTab({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const q = useQuery(projectCardQuery(project.id));
  const apply = (v: ProjectCardView) => {
    qc.setQueryData(projectCardQuery(project.id).queryKey, v);
    qc.invalidateQueries({ queryKey: ['attention'] });
    qc.invalidateQueries({ queryKey: ['portfolio'] });
  };
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  return (
    <div className="flex flex-col gap-6">
      <div
        className={clsx(
          'rounded-lg border px-5 py-4 text-[14px]',
          v.preliminary && !v.obligationsAllowed
            ? 'border-warning/50 bg-warning-soft'
            : 'border-line bg-surface',
        )}
      >
        {v.revenueContract ? (
          <p>
            Доходный договор <b>{v.revenueContract.number}</b> подписан {date(v.revenueContract.signedDate)}
            {v.revenueContract.amount !== null ? ` на ${tenge(v.revenueContract.amount)}` : ''}.
            {v.task ? (
              <span className={clsx('ml-1', v.task.overdue && 'font-medium text-danger')}>
                {v.task.done
                  ? 'Карточка проекта и финансовый план оформлены.'
                  : `Карточка проекта и финансовый план — до ${date(v.task.due)} (5 рабочих дней, ПФ-25).`}
              </span>
            ) : null}
          </p>
        ) : v.preliminary ? (
          <p>
            <b>Преддоговорная стадия.</b> КП, расчёты и договорённости с заказчиком предварительные; заявки,
            расходные договоры и мобилизация по проекту{' '}
            {v.obligationsAllowed
              ? 'разрешены одобренной служебной запиской.'
              : 'не создаются до подписания доходного договора (ПФ-23).'}
          </p>
        ) : (
          <p>
            Доходный договор в портал не внесён: добавьте его в разделе «Договоры» с видом «Доходный, с
            заказчиком».
          </p>
        )}
      </div>
      <Precontract v={v} project={project} onSaved={apply} />
      <Exception v={v} project={project} onSaved={apply} />
      <Financing v={v} project={project} onSaved={apply} />
      <Card v={v} project={project} onSaved={apply} />
    </div>
  );
}
